/**
 * Company: SPHMedia
 * Description: [Toggle button]
 */

import { ReactElement, useState } from "react";

type ToggleButtonProps = {
  onToggle: (enabled: boolean) => void;
  defaultEnabled?: boolean;
  variant?: "small" | "default";
};

/**
 * ToggleButton
 * Description: [Toggle button component mainly for notification enable/disable block]
 * @param props - The props required by the component (if any)
 * @returns The rendered React element
 */
export default function ToggleButton({
  onToggle,
  defaultEnabled = false,
  variant = "default",
}: ToggleButtonProps): ReactElement {
  const [enabled, setEnabled] = useState(defaultEnabled);

  const handleToggle = () => {
    const newValue = !enabled;
    setEnabled(newValue);
    onToggle(newValue);
  };

  const isSmall = variant === "small";
  const buttonStyle = isSmall ? "w-[60px] h-[33px]" : "w-[75px] h-10";
  const sliderStyle = isSmall
    ? "w-[25.6px] h-[25.6px] rounded-[25.6px]"
    : "w-8 h-8 rounded-[32px]";

  return (
    <button
      data-testid="toggle-button"
      onClick={handleToggle}
      aria-pressed={enabled}
      className={`relative ${buttonStyle} rounded-[500px] shrink-0 transition ${
        enabled ? "bg-lightBlue-50" : "bg-grey-1000"
      }`}
    >
      <span
        className={`absolute top-1 left-1 ${sliderStyle} transition ${
          enabled
            ? `${isSmall ? "translate-x-[25.6px]" : "translate-x-8"} bg-blue-400`
            : "bg-grey-130"
        }`}
      />
    </button>
  );
}
